/**
 * Tableau du plan média BF 26 : les cibles de chaque tranche, telles que
 * présentées par l'agence. Les tranches touchées par la période choisie sont
 * surlignées : ce sont elles qui fixent les cibles des tuiles au-dessus.
 */

import { BF26_PLAN, type Bf26Slice } from '../lib/bf26';
import { formatInteger, formatMoney } from '../lib/format';
import { Card } from './Card';

const DAY = 86_400_000;
const daysOf = (slice: Pick<Bf26Slice, 'from' | 'to'>) => Math.round((Date.parse(slice.to) - Date.parse(slice.from)) / DAY) + 1;
const euros = (value: number) => formatMoney(Math.round(value), 'EUR');
const kEuros = (value: number) => `${(value / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} k€`;
const ratio = (value: number) => `×${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Row({ label, slice, active, strong }: { label: string; slice: Omit<Bf26Slice, 'label'>; active?: boolean; strong?: boolean }) {
  const days = daysOf(slice);
  const cell = (text: string) => <td className="num">{strong ? <strong>{text}</strong> : text}</td>;
  return (
    <tr style={active ? { background: 'var(--surface-raised)' } : undefined}>
      <td>
        {strong ? <strong>{label}</strong> : label}
        {active && <span className="kpi__hint"> · période choisie</span>}
      </td>
      {cell(euros(slice.budget))}
      {cell(euros(slice.budget / days))}
      {cell(euros(slice.budget / slice.orders))}
      {cell(formatInteger(slice.orders))}
      {cell(formatInteger(Math.round(slice.orders / days)))}
      {cell(kEuros(slice.revenueHt))}
      {cell(ratio(slice.revenueHt / slice.budget))}
    </tr>
  );
}

const sumOf = (slices: readonly Bf26Slice[]) => ({
  from: slices[0]?.from ?? '',
  to: slices[slices.length - 1]?.to ?? '',
  budget: slices.reduce((n, s) => n + s.budget, 0),
  orders: slices.reduce((n, s) => n + s.orders, 0),
  revenueHt: slices.reduce((n, s) => n + s.revenueHt, 0),
});

export function Bf26PlanTable({ activeLabels }: { activeLabels: ReadonlySet<string> }) {
  const total = sumOf(BF26_PLAN);
  const ordersIn = (month: string) => BF26_PLAN.filter((s) => s.from.startsWith(month)).reduce((n, s) => n + s.orders, 0);
  return (
    <Card
      title="Le plan média"
      subtitle={`Objectif : ${formatInteger(total.orders)} commandes — ${formatInteger(ordersIn('2026-11'))} en novembre · ${formatInteger(ordersIn('2026-12'))} en décembre. MER cible = CA produits HT ÷ pub totale de la période. Le CPA cible est un plafond moyen.`}
      className="chart-grid__wide"
    >
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Période</th>
              <th className="num">Budget pub</th>
              <th className="num">Par jour</th>
              <th className="num">CPA cible</th>
              <th className="num">Commandes cibles</th>
              <th className="num">Par jour</th>
              <th className="num">CA produits HT</th>
              <th className="num">MER cible</th>
            </tr>
          </thead>
          <tbody>
            {BF26_PLAN.map((slice) => (
              <Row key={slice.label} label={slice.label} slice={slice} active={activeLabels.has(slice.label)} />
            ))}
            <Row label="Total" slice={total} strong />
          </tbody>
        </table>
      </div>
    </Card>
  );
}
